import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { GRADES, gradeLabel, gradeDescriptor, canManage } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, ClipboardList, FolderOpen, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";

const iconFor = { safeguarding: "Shield", inclusion: "Users", leadership_governance: "Building2", skills_needs: "Briefcase", curriculum_teaching: "BookOpen", achievement: "Award", participation_development: "GraduationCap" };

function Stat({ icon: Icon, label, value, tone = "indigo" }) {
  const tones = { indigo: "bg-indigo-50 text-indigo-800", emerald: "bg-emerald-50 text-emerald-800", amber: "bg-amber-50 text-amber-800", rose: "bg-rose-50 text-rose-800" };
  return (
    <Card className="p-5 border-slate-200">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
        <div className={`h-8 w-8 rounded-md grid place-items-center ${tones[tone]}`}><Icon className="h-4 w-4" /></div>
      </div>
      <div className="mt-3 text-3xl font-bold font-mono tracking-tight text-slate-900">{value}</div>
    </Card>
  );
}

function ReadinessGauge({ score, breakdown }) {
  const tone = score >= 75 ? "emerald" : score >= 50 ? "indigo" : score >= 30 ? "amber" : "rose";
  const bg = { emerald: "from-emerald-600 to-emerald-800", indigo: "from-indigo-700 to-indigo-950", amber: "from-amber-500 to-amber-700", rose: "from-rose-600 to-rose-800" }[tone];
  return (
    <Card className={`p-5 border-slate-200 bg-gradient-to-br ${bg} text-white col-span-2`} data-testid="readiness-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-white/70">Inspection-readiness score</div>
          <div className="mt-1 text-6xl font-extrabold tracking-tight font-mono">{score}<span className="text-2xl font-medium text-white/70">/100</span></div>
          <div className="text-[11px] text-white/70 mt-1">Weighted composite · Grades 40% · RAG health 30% · Evidence density 30%</div>
        </div>
        <div className="text-right space-y-1 text-xs">
          <div className="flex items-center gap-2"><span className="w-14 text-white/70">Grades</span><span className="font-mono font-bold">{breakdown.grade}</span></div>
          <div className="flex items-center gap-2"><span className="w-14 text-white/70">RAG</span><span className="font-mono font-bold">{breakdown.rag}</span></div>
          <div className="flex items-center gap-2"><span className="w-14 text-white/70">Evidence</span><span className="font-mono font-bold">{breakdown.evidence}</span></div>
        </div>
      </div>
      <div className="mt-4 h-2 rounded-full bg-white/15 overflow-hidden">
        <div className="h-full bg-white transition-all" style={{ width: `${score}%` }} />
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/dashboard/summary"); setData(r.data); }
    catch (e) { toast.error("Failed to load dashboard"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const updateGrade = async (key, current_grade) => {
    try { await api.patch(`/themes/${key}`, { current_grade }); toast.success("Grade updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  if (loading || !data) return <div className="text-slate-500">Loading dashboard…</div>;

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-indigo-800">Overview · Cycle 2025/26</div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "Plus Jakarta Sans" }}>Quality improvement, at a glance</h1>
        <p className="text-slate-600 mt-1">All seven Ofsted evaluation areas · evidence, actions and current graded judgements.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-5">
        <ReadinessGauge score={data.totals.readiness_score || 0} breakdown={data.totals.readiness_breakdown || { grade: 0, rag: 0, evidence: 0 }} />
        <Stat icon={ClipboardList} label="QIP actions" value={data.totals.actions} />
        <Stat icon={CheckCircle2} label="Completed" value={data.totals.completed_actions} tone="emerald" />
        <Stat icon={FolderOpen} label="Evidence" value={data.totals.evidence} />
        <Stat icon={AlertTriangle} label="Themes at risk" value={data.totals.themes_at_risk} tone="rose" />
      </div>

      <Card className="p-5 border-slate-200">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Plus Jakarta Sans" }}>Ofsted grading scale</h2>
            <p className="text-xs text-slate-500">Descriptors from the Ofsted FE & Skills Inspection Toolkit v2.0 (September 2026)</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
          {GRADES.map((g) => (
            <div key={g.key} className={`grade-${g.key} rounded-md p-3 border`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="h-2 w-2 rounded-full" style={{ background: g.dot }} />
                <div className="text-xs font-bold uppercase tracking-wider">{g.label}</div>
              </div>
              <div className="text-[11px] leading-snug opacity-90">{g.description}</div>
            </div>
          ))}
        </div>
      </Card>

      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: "Plus Jakarta Sans" }}>Ofsted evaluation areas</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {data.themes.map((t) => {
            const s = data.by_theme[t.key];
            return (
              <Card key={t.key} data-testid={`theme-card-${t.key}`} className="p-5 border-slate-200 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{t.code}</div>
                    <div className="text-lg font-bold text-slate-900 mt-1" style={{ fontFamily: "Plus Jakarta Sans" }}>{t.title}</div>
                  </div>
                  <Badge className={`grade-${t.current_grade} font-medium border`}>{gradeLabel(t.current_grade)}</Badge>
                </div>
                <p className="text-xs text-slate-600 mt-2 leading-relaxed line-clamp-2">{t.description}</p>
                <div className="mt-4 flex items-center gap-2 text-xs">
                  <span className="text-slate-500">Progress</span>
                  <Progress value={s.avg_progress} className="h-2 flex-1" />
                  <span className="font-mono font-semibold text-slate-800">{s.avg_progress}%</span>
                </div>
                <div className="mt-3 flex items-center gap-3 text-xs text-slate-600">
                  <span><b className="text-slate-900">{s.actions_total}</b> actions</span>
                  <span className="text-slate-300">·</span>
                  <span><b className="text-slate-900">{s.evidence_total}</b> evidence</span>
                </div>
                <div className="mt-3 flex gap-1">
                  {Object.entries(s.rag_counts).map(([k, v]) => v > 0 && (
                    <div key={k} className={`rag-${k} rounded px-2 py-0.5 text-[11px] font-semibold`}>{v}</div>
                  ))}
                </div>
                {canManage(user?.role) && (
                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500">Set grade</span>
                    <Select value={t.current_grade} onValueChange={(v) => updateGrade(t.key, v)}>
                      <SelectTrigger data-testid={`grade-select-${t.key}`} className="h-7 text-xs w-auto flex-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{GRADES.map((g) => (<SelectItem key={g.key} value={g.key}>{g.label}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="mt-3 flex gap-2">
                  <Link to={`/actions?theme=${t.key}`} className="text-xs font-semibold text-indigo-800 hover:underline">View actions →</Link>
                  <Link to={`/actions?theme=${t.key}&suggest=1`} className="text-xs font-semibold text-emerald-800 hover:underline">Suggested ({t.suggested_actions_count || 0}) →</Link>
                  <Link to={`/sar?theme=${t.key}`} className="ml-auto text-xs font-semibold text-indigo-800 hover:underline">Open SAR →</Link>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
